/**
 * img.js — image delivery optimizer
 *
 * Source assets are PNG/JPG (1–3 MB). We route every <img> through
 * weserv.nl's free image proxy which transcodes to WebP, resizes to a
 * target width and serves at the requested quality. Result is typically
 * 5–15× smaller than the source and is edge-cached.
 *
 * Usage:
 *   <img
 *     src={optimizedImg(p.image, 800)}
 *     srcSet={srcSetFor(p.image)}
 *     sizes="(max-width: 768px) 50vw, 25vw"
 *     loading="lazy"
 *     decoding="async"
 *   />
 *
 * For above-the-fold hero shots, pass loading="eager" + fetchpriority="high".
 */

const PROXY = "https://images.weserv.nl/";

/** Wrap any absolute URL with the proxy. Returns input unchanged for
 *  relative paths, data URIs, or already-proxied URLs. */
export function optimizedImg(url, width = 800, quality = 82) {
  if (!url) return "";
  if (typeof url !== "string") return url;
  if (!url.startsWith("http")) return url; // local /public assets, data:, etc.
  if (url.includes("images.weserv.nl")) return url;
  // weserv decodes the `url` query param once, so any `%`-encoded char inside
  // the source URL (e.g. `%23` from a `#` in the filename) gets stripped down
  // to its literal form before being passed on to origin — origin then sees
  // it as a URL fragment and returns 403/404. Pre-double-encode `%` so the
  // round-trip lands the right bytes at origin.
  const stripped = url
    .replace(/^https?:\/\//, "")
    .replace(/%/g, "%25");
  const params = new URLSearchParams({
    url: stripped,
    w: String(width),
    output: "webp",
    q: String(quality),
    we: "1",
  });
  return `${PROXY}?${params.toString()}`;
}

/** Build a srcSet across common breakpoints so the browser can pick the
 *  smallest image that satisfies the layout slot. */
export function srcSetFor(url, widths = [320, 480, 640, 800, 1200, 1600]) {
  if (!url || typeof url !== "string" || !url.startsWith("http")) return undefined;
  return widths.map((w) => `${optimizedImg(url, w)} ${w}w`).join(", ");
}
